import { motion } from "framer-motion";
import { Clock, CreditCard, Facebook, Mail, MapPin, Navigation, Phone } from "lucide-react";

const AREAS = ["Rittman", "Cleveland", "Wadsworth", "Norton", "Medina", "Wooster", "Akron", "Brunswick", "Massillon", "Cuyahoga Falls"];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function LocationSection() {
  return (
    <section id="contact" data-testid="location-section" className="bg-ink py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember" data-testid="location-overline">
          Home Base
        </p>
        <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl" data-testid="location-heading">
          Rittman, Ohio
        </h2>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            data-testid="location-address-card"
            className="border border-white/10 bg-surface p-8"
          >
            <MapPin className="h-7 w-7 text-ember" />
            <h3 className="mt-5 font-display text-2xl font-bold uppercase text-white">Find Us</h3>
            <div className="mt-3 space-y-5" data-testid="location-address">
              <div>
                <p className="text-sm leading-relaxed text-slate-400">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Rittman — Home Base<br /></span>
                  Flatlander Trucking LLC<br />
                  10010 Sterling Rd<br />Rittman, OH 44270
                </p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=10010+Sterling+Rd+Rittman+OH+44270"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="location-directions-link"
                  className="mt-3 inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white transition-colors duration-200 hover:border-ember hover:text-ember"
                >
                  <Navigation className="h-3.5 w-3.5" /> Directions
                </a>
              </div>
              <div className="border-t border-white/10 pt-5">
                <p className="text-sm leading-relaxed text-slate-400">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Cleveland Yard<br /></span>
                  10630 Berea Rd<br />Cleveland, OH
                </p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=10630+Berea+Rd+Cleveland+OH"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="location-directions-cleveland-link"
                  className="mt-3 inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white transition-colors duration-200 hover:border-ember hover:text-ember"
                >
                  <Navigation className="h-3.5 w-3.5" /> Directions
                </a>
              </div>
            </div>
            <div className="mt-8 border-t border-white/10 pt-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Proudly Serving</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="location-service-areas">
                {AREAS.map((a) => (
                  <span key={a} className="border border-white/10 bg-ink px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-slate-300">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.08 }}
            data-testid="location-hours-card"
            className="border border-white/10 bg-surface p-8"
          >
            <Clock className="h-7 w-7 text-ember" />
            <h3 className="mt-5 font-display text-2xl font-bold uppercase text-white">Hours</h3>
            <ul className="mt-3 space-y-1.5" data-testid="location-hours-list">
              {DAYS.map((d) => (
                <li key={d} className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">{d}</span>
                  <span className="font-mono text-xs uppercase tracking-wider text-ember">Open 24 hours</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.16 }}
            data-testid="location-contact-card"
            className="border border-white/10 bg-surface p-8"
          >
            <Phone className="h-7 w-7 text-ember" />
            <h3 className="mt-5 font-display text-2xl font-bold uppercase text-white">Reach Dispatch</h3>
            <a href="tel:+13302014612" data-testid="location-phone-link" className="mt-3 block font-display text-3xl font-extrabold text-white transition-colors duration-200 hover:text-ember">
              +1-330-201-4612
            </a>
            <div className="mt-6 space-y-3 text-sm text-slate-400">
              <p className="flex items-center gap-2" data-testid="location-payments-note">
                <CreditCard className="h-4 w-4 text-slate-500" /> Credit &amp; debit cards accepted
              </p>
              <a
                href="mailto:flatlander_trucking@yahoo.com"
                data-testid="location-email-link"
                className="flex items-center gap-2 transition-colors duration-200 hover:text-ember"
              >
                <Mail className="h-4 w-4 text-slate-500" /> flatlander_trucking@yahoo.com
              </a>
              <a
                href="https://www.facebook.com/p/Flatlander-trucking-llc-100076240866675/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-facebook-link"
                className="flex items-center gap-2 transition-colors duration-200 hover:text-ember"
              >
                <Facebook className="h-4 w-4 text-slate-500" /> Flatlander Trucking LLC on Facebook
              </a>
              <p className="text-xs text-slate-500" data-testid="location-accessibility-note">
                Wheelchair accessible parking lot on site
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
